import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  // 🚩 FEATURE FLAG: Set to false to disable all onboarding
  const ONBOARDING_ENABLED = false;
  
  const { user, userIsPremium } = useAuth();
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState(0);
  const [hasSeenWelcome, setHasSeenWelcome] = useState(false);

  // Check if user should see onboarding
  useEffect(() => {
    // 🚩 Only use feature flag - ignore localStorage completely
    if (!ONBOARDING_ENABLED) {
      setShouldShowOnboarding(false);
      setHasCompletedOnboarding(true);
      return;
    }

    if (!user || !userIsPremium) {
      setShouldShowOnboarding(false);
      return;
    }

    // When enabled, always show onboarding for premium users
    setHasCompletedOnboarding(false);
    setShouldShowOnboarding(true);
  }, [user, userIsPremium, ONBOARDING_ENABLED]);

  const startOnboarding = () => {
    // 🚩 Don't start if disabled
    if (!ONBOARDING_ENABLED) return;
    
    setIsOnboardingActive(true);
    setShouldShowOnboarding(true);
    // Add class to body to prevent scrolling
    document.body.classList.add('onboarding-active');
  };

  const completeOnboarding = () => {
    // 🚩 Don't store to localStorage - just update state
    setIsOnboardingActive(false);
    setShouldShowOnboarding(false);
    setHasCompletedOnboarding(true);
    
    // Remove body class
    document.body.classList.remove('onboarding-active');
  };

  const skipOnboarding = () => {
    completeOnboarding(); // Same as completing for now
  };

  const resetOnboarding = () => {
    // 🚩 Only reset state - no localStorage operations
    setHasCompletedOnboarding(false);
    setShouldShowOnboarding(ONBOARDING_ENABLED);
  };

  // Auto-start onboarding for eligible users
  useEffect(() => {
    if (shouldShowOnboarding && !isOnboardingActive && !hasCompletedOnboarding) {
      // Add a small delay to ensure the page has loaded
      const timer = setTimeout(() => {
        startOnboarding();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [shouldShowOnboarding, isOnboardingActive, hasCompletedOnboarding]);

  const updateProgress = (stepIndex, totalSteps) => {
    const progress = ((stepIndex + 1) / totalSteps) * 100;
    setOnboardingProgress(progress);
    
    // 🚩 Don't store progress to localStorage
  };

  const markWelcomeSeen = () => {
    setHasSeenWelcome(true);
    // 🚩 Don't store welcome seen to localStorage
  };

  const value = {
    shouldShowOnboarding,
    isOnboardingActive,
    hasCompletedOnboarding,
    onboardingProgress,
    hasSeenWelcome,
    startOnboarding,
    completeOnboarding,
    skipOnboarding,
    resetOnboarding,
    updateProgress,
    markWelcomeSeen,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider;
